import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from 'react';
import * as Sentry from '@sentry/react';
import { Button, Spin, Space, Slider } from 'antd';
import { createSafeS3Url } from 'shared/utils';
import {
  ReloadOutlined,
  CloseOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import CustomPdfViewer from './CustomPdfViewer';

interface DocumentViewerProps {
  media?: string | File;
  isFixed?: boolean;
  fixedWidth?: number;
  fixedLeft?: number;
  onClose?: () => void;
  skipContainerStyle?: boolean;
}

/**
 * Transform URL and determine previewer type
 * For existing URLs: Convert to optimized PDF if possible
 * For file objects: Create local URL and determine type
 */
const transformUrl = async (input: string | File) => {
  // If input is a File object, create local URL and determine type
  if (input instanceof File) {
    const localUrl = URL.createObjectURL(input);

    // Determine previewer type based on file type
    const isImage = input.type.startsWith('image/');
    const previewerType = isImage ? 'image' : 'pdf';

    return {
      url: localUrl,
      previewerType,
    };
  }

  // If input is a string URL, try to convert to optimized PDF
  if (typeof input === 'string') {
    // Handle blob URLs - check the actual file type
    if (input.startsWith('blob:')) {
      try {
        // Fetch the blob to check its type
        const response = await fetch(input);
        const blob = await response.blob();

        // Determine previewer type based on actual blob type
        const isImage = blob.type.startsWith('image/');
        const previewerType = isImage ? 'image' : 'pdf';

        return {
          url: input,
          previewerType,
        };
      } catch (error) {
        console.error('❌ Error checking blob type:', error);
        // Fallback to PDF if we can't determine the type
        Sentry.captureException(error, {
          tags: {
            component: 'DocumentViewer',
            method: 'transformUrlBlob',
            url: input,
          },
          extra: {
            url: input,
          },
        });

        return {
          url: input,
          previewerType: 'pdf',
        };
      }
    }

    try {
      const cleanUrl = createSafeS3Url(input);
      const urlParts = cleanUrl.split('/');
      const originalFileName = urlParts[urlParts.length - 1];

      const baseFileName = originalFileName.split('.')[0];
      const optimizedFileName = `${baseFileName}_optimized.pdf`;

      const baseUrl = cleanUrl.substring(0, cleanUrl.lastIndexOf('/'));

      const urlObj = new URL(baseUrl);
      const bucketAndRegion = `${urlObj.hostname}`;

      let optimizedPdfUrl = `https://${bucketAndRegion}/`;

      if (cleanUrl.includes('UFP-Workorder/production/')) {
        optimizedPdfUrl += 'opt-pdf/';
        optimizedPdfUrl += optimizedFileName;
        return {
          url: optimizedPdfUrl,
          previewerType: 'pdf',
        };
      } else if (cleanUrl.includes('/staging/')) {
        optimizedPdfUrl += 'opt-pdf/staging/';
        optimizedPdfUrl += optimizedFileName;
        return {
          url: optimizedPdfUrl,
          previewerType: 'pdf',
        };
      } else {
        // Determine previewer type based on file extension in URL
        const fileExtension = originalFileName.split('.').pop()?.toLowerCase();
        let previewerType = 'pdf'; // Default to PDF

        if (fileExtension) {
          if (['jpg', 'jpeg', 'png'].includes(fileExtension)) {
            previewerType = 'image';
          }
        }

        return {
          url: cleanUrl,
          previewerType: previewerType,
        };
      }
    } catch (error) {
      console.error('❌ Error transforming URL:', error);
      Sentry.captureException(error, {
        tags: {
          component: 'DocumentViewer',
          method: 'transformUrlOther',
          url: input,
        },
        extra: {
          url: input,
        },
      });
      return {
        url: input, // Fallback to original URL
        previewerType: 'pdf',
      };
    }
  }

  // Fallback
  return {
    url: String(input),
    previewerType: 'pdf',
  };
};

/**
 * Convert data to proper format for react-doc-viewer
 */
const prepareDocumentData = async (media: string | File) => {
  if (
    media instanceof File ||
    media.startsWith('http://') ||
    media.startsWith('https://') ||
    media.startsWith('blob:')
  ) {
    const fileName =
      media instanceof File ? media.name : media.split('/').pop() || 'document';

    const { url, previewerType } = await transformUrl(media);
    const documentData = [
      {
        uri: url,
        fileName:
          previewerType === 'pdf'
            ? fileName.replace(/\.[^/.]+$/, '.pdf')
            : fileName,
        fileType: previewerType === 'pdf' ? 'application/pdf' : 'image/*',
      },
    ];

    return { documentData, previewerType };
  }

  return { documentData: [], previewerType: 'pdf' };
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  media,
  isFixed,
  fixedWidth,
  fixedLeft,
  onClose,
  skipContainerStyle = false,
}) => {
  const [documentData, setDocumentData] = useState<any[]>([]);
  const [previewerType, setPreviewerType] = useState('pdf');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const blobUrlRef = useRef<string | null>(null);

  // Extract filename from media
  const getFilename = () => {
    if (media instanceof File) {
      return media.name.replace(/\.[^/.]+$/, ''); // Remove file extension
    }
    if (typeof media === 'string') {
      try {
        const url = new URL(media);
        const pathname = url.pathname;
        const filename = pathname.split('/').pop() || 'Document';
        return filename.replace(/\.[^/.]+$/, ''); // Remove file extension
      } catch {
        return 'Document';
      }
    }
    return 'Document';
  };

  // Remove memoization since prepareDocumentData is now async

  useEffect(() => {
    if (!media) {
      console.log(
        '⚠️ DocumentViewer no media provided, setting loading to false'
      );
      setLoading(false);
      return;
    }

    const loadDocument = async () => {
      try {
        setLoading(true);
        setError(null);

        // Prepare document data - transform URL and get previewer type
        const { documentData: docs, previewerType: type } =
          await prepareDocumentData(media);

        // Check if the blob URL is still valid
        if (docs[0]?.uri && docs[0].uri.startsWith('blob:')) {
          try {
            // Test if the blob URL is still accessible
            const response = await fetch(docs[0].uri);
            if (!response.ok) {
              throw new Error('Blob URL is no longer valid');
            }
            // Track blob URL for cleanup if it's valid
            blobUrlRef.current = docs[0].uri;
          } catch (blobError) {
            // If blob URL is invalid, recreate it for File objects

            if (media instanceof File) {
              const newBlobUrl = URL.createObjectURL(media);
              docs[0].uri = newBlobUrl;
              blobUrlRef.current = newBlobUrl;
            }
          }
        }

        setDocumentData(docs);
        setPreviewerType(type);
        setLoading(false);
      } catch (err) {
        console.error('❌ Error preparing document viewer:', err);
        setError('Failed to load document');
        setLoading(false);
        console.log('❌ DocumentViewer loadDocument failed');
        Sentry.captureException(err, {
          tags: {
            component: 'DocumentViewer',
            method: 'loadDocument',
          },
          extra: {
            url: media,
          },
        });
      }
    };

    loadDocument();

    // Cleanup function to revoke object URLs when component unmounts or media changes
    return () => {
      // Revoke the tracked blob URL if it exists
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [media]);

  // Handle retry
  const handleRetry = async () => {
    setRetryCount((prev) => prev + 1);
    setLoading(true);
    setError(null);

    // Force re-render by updating document data
    setTimeout(async () => {
      const { documentData: docs, previewerType: type } =
        await prepareDocumentData(media || '');
      setDocumentData(docs);
      setPreviewerType(type);
      setLoading(false);
    }, 100);
  };

  // Container style that handles fixed positioning
  const containerStyle: React.CSSProperties = skipContainerStyle
    ? {}
    : isFixed
      ? {
          position: 'sticky',
          top: 30,
          left: fixedLeft ? `${fixedLeft}px` : 0,
          width: fixedWidth ? `${fixedWidth}px` : 'auto',
          maxWidth: fixedWidth ? `${fixedWidth}px` : '600px',
          zIndex: 1000,
          overflow: 'hidden',
          boxSizing: 'border-box',
          backgroundColor: 'white',
          border: '1px solid #d9d9d9',
          borderRadius: '6px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        }
      : {
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    console.log('🎬 DocumentViewer rendering error state:', error);
    return (
      <div style={containerStyle}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <div style={{ color: '#ff4d4f' }}>{error}</div>
          <Space>
            <Button onClick={handleRetry} icon={<ReloadOutlined />}>
              Retry
            </Button>
            {onClose && <Button onClick={onClose}>Close</Button>}
          </Space>
        </div>
      </div>
    );
  }

  if (documentData.length === 0) {
    return null;
  }

  // Document viewer with dual previewer support
  return (
    <div style={containerStyle}>
      {/* Document viewer controls */}
      <div
        style={{
          padding: '8px 12px',
          borderBottom: '1px solid #f0f0f0',
          backgroundColor: '#fafafa',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {getFilename()}
        </span>

        {/* Retry button if there's an error */}
        {error && (
          <Button size="small" icon={<ReloadOutlined />} onClick={handleRetry}>
            Retry
          </Button>
        )}

        <Button
          size="small"
          onClick={() => {
            if (typeof media === 'string') {
              const link = document.createElement('a');
              link.href = media;
              link.download = 'document';
              link.click();
            }
          }}
          title="Download original file"
          disabled={typeof media !== 'string'}
        >
          Download
        </Button>

        {onClose && (
          <Button size="small" onClick={onClose}>
            <CloseOutlined />
          </Button>
        )}
      </div>

      {/* Universal document viewer */}
      <div
        style={{
          width: '100%',
          height: 'calc(100vh - 200px)',
          minHeight: '400px',
          overflow: 'auto',
          position: 'relative',
        }}
      >
        {previewerType === 'image' ? (
          // Image viewer with zoom controls
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              padding: '20px',
            }}
          >
            {/* Zoom controls */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '16px',
                padding: '8px 12px',
                backgroundColor: '#f5f5f5',
                borderRadius: '6px',
              }}
            >
              <span style={{ fontSize: '12px', fontWeight: '500' }}>Zoom:</span>
              <Button
                size="small"
                icon={<ZoomOutOutlined />}
                onClick={() =>
                  setImageZoom((prev) => Math.max(0.5, prev - 0.25))
                }
                disabled={imageZoom <= 0.5}
              />
              <Slider
                min={0.5}
                max={3}
                step={0.25}
                value={imageZoom}
                onChange={(value) => setImageZoom(value || 1)}
                style={{ width: '120px', margin: '0 8px' }}
                tooltip={{
                  formatter: (value) => `${Math.round((value || 1) * 100)}%`,
                }}
              />
              <Button
                size="small"
                icon={<ZoomInOutlined />}
                onClick={() => setImageZoom((prev) => Math.min(3, prev + 0.25))}
                disabled={imageZoom >= 3}
              />
              <Button
                size="small"
                onClick={() => setImageZoom(1)}
                style={{ marginLeft: '8px' }}
              >
                Reset
              </Button>
              <span style={{ fontSize: '12px', color: '#666' }}>
                {Math.round(imageZoom * 100)}%
              </span>
            </div>

            {/* Image container with zoom */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: 'auto',
                border: '1px solid #e8e8e8',
                borderRadius: '6px',
                backgroundColor: '#fafafa',
              }}
            >
              <img
                src={documentData[0]?.uri}
                alt="Document Preview"
                style={{
                  maxWidth: 'none',
                  maxHeight: 'none',
                  width: `${imageZoom * 100}%`,
                  height: 'auto',
                  objectFit: 'contain',
                  transition: 'width 0.2s ease-in-out',
                }}
                onLoad={() => {}}
                onError={(e) => {
                  console.error('❌ DocumentViewer image failed to load:', e);
                  console.error(
                    '❌ DocumentViewer image src was:',
                    documentData[0]?.uri
                  );
                  setError('Failed to load image');
                }}
              />
            </div>
          </div>
        ) : (
          // PDF viewer with proper multi-page support
          <CustomPdfViewer
            fileUrl={documentData[0]?.uri}
            height="100%"
            width="100%"
          />
        )}
      </div>
    </div>
  );
};

export default DocumentViewer;
